/**
 * Summary page — AI-powered sheet/selection summarization.
 *
 * Reads the current Excel selection (or used range), sends it to the
 * chat endpoint with a summarization prompt, and displays a structured summary.
 */

import React, { useState } from 'react';
import {
  Button,
  Text,
  Badge,
  Textarea,
} from '@fluentui/react-components';
import {
  DocumentSearchRegular,
  TextBulletListSquareRegular,
  ArrowResetRegular,
} from '@fluentui/react-icons';
import { sendChat } from '../services/api.client';
import { readSelectionInfo, type SelectionInfo } from '../office';
import { getSettings } from '../store/settings.store';
import { addHistoryEntry } from '../store/history.store';
import { LoadingSpinner } from '../components/common/LoadingSpinner';
import { ErrorDisplay } from '../components/common/ErrorDisplay';

export const SummaryPage: React.FC = () => {
  const [selectionInfo, setSelectionInfo] = useState<SelectionInfo>();
  const [loading, setLoading] = useState(false);
  const [summary, setSummary] = useState<string>();
  const [error, setError] = useState<string>();

  const resetAll = () => {
    setSelectionInfo(undefined);
    setSummary(undefined);
    setError(undefined);
  };

  const handleReadSelection = async () => {
    setError(undefined);
    setSummary(undefined);
    setLoading(true);

    try {
      const info = await readSelectionInfo();
      if (!info.values || info.values.length === 0) {
        setError('Aucune cellule selectionnee. Selectionnez des donnees dans Excel.');
        setSelectionInfo(undefined);
        return;
      }
      setSelectionInfo(info);
    } catch (err) {
      setError((err as Error).message);
      setSelectionInfo(undefined);
    } finally {
      setLoading(false);
    }
  };

  const handleSummarize = async () => {
    if (!selectionInfo) return;
    const startedAt = Date.now();
    setError(undefined);
    setSummary(undefined);
    setLoading(true);

    const settings = getSettings();

    try {
      // Build a textual representation of the selection
      const preview = selectionInfo.values
        .slice(0, 50)
        .map((row) => (row as unknown[]).map((c) => (c == null ? '' : String(c))).join(' | '))
        .join('\n');

      const truncated = selectionInfo.values.length > 50
        ? `\n... (${selectionInfo.values.length - 50} lignes supplementaires)`
        : '';

      const prompt =
        `Resume de facon structuree les donnees suivantes (${selectionInfo.rowCount} lignes, ${selectionInfo.colCount} colonnes, plage ${selectionInfo.address}):\n\n` +
        preview +
        truncated;

      const response = await sendChat({
        messages: [{ role: 'user', content: prompt, timestamp: Date.now() }],
      });
      setSummary(response.content);
      addHistoryEntry(
        'analysis',
        settings.provider,
        settings.model,
        'Resume de feuille',
        true,
        undefined,
        {
          module: 'summary',
          durationMs: Date.now() - startedAt,
          rangeAddress: selectionInfo.address,
        },
      );
    } catch (err) {
      setError((err as Error).message);
      addHistoryEntry(
        'analysis',
        settings.provider,
        settings.model,
        'Resume de feuille',
        false,
        (err as Error).message,
        {
          module: 'summary',
          durationMs: Date.now() - startedAt,
          rangeAddress: selectionInfo.address,
        },
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <div className="card">
        <Text className="section-title">Resumer la feuille</Text>
        <Text size={200} style={{ color: 'var(--text-secondary)', display: 'block', marginBottom: 8 }}>
          Selectionnez des donnees dans Excel. L&apos;IA generera un resume structure
          de votre selection : contenu, tendances, points cles.
        </Text>

        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <Button
            size="small"
            appearance="primary"
            icon={<DocumentSearchRegular />}
            onClick={handleReadSelection}
            disabled={loading}
          >
            Lire la selection
          </Button>
          {selectionInfo && !loading && (
            <Button
              size="small"
              appearance="primary"
              icon={<TextBulletListSquareRegular />}
              onClick={handleSummarize}
            >
              Resumer
            </Button>
          )}
          {(selectionInfo || summary) && (
            <Button size="small" appearance="subtle" icon={<ArrowResetRegular />} onClick={resetAll}>
              Reinitialiser
            </Button>
          )}
        </div>

        {selectionInfo && (
          <div style={{ marginTop: 8, display: 'flex', gap: 8, alignItems: 'center' }}>
            <Badge appearance="outline" size="small" color="informative">
              {selectionInfo.address}
            </Badge>
            <Text size={200} style={{ color: 'var(--text-secondary)' }}>
              {selectionInfo.rowCount} lignes x {selectionInfo.colCount} colonnes
            </Text>
          </div>
        )}
      </div>

      {loading && <LoadingSpinner label="Generation du resume..." />}
      {error && <ErrorDisplay message={error} onDismiss={() => setError(undefined)} />}

      {summary && (
        <div className="card">
          <Text className="section-title">Resume</Text>
          <Textarea
            value={summary}
            readOnly
            resize="vertical"
            style={{ width: '100%', minHeight: 200, fontFamily: 'inherit' }}
          />
        </div>
      )}
    </div>
  );
};
